import {
  applyNativeWebViewAutocaptureGuards,
  WEB_LEGACY_NATIVE_HOSTED_DEFAULT_TRACKING,
  WEB_NATIVE_HOSTED_DEFAULT_TRACKING,
  type WebAutocaptureOptions,
  type WebDefaultTrackingFlags,
} from './webviewAutocaptureOwnership';

export type {
  WebAutocaptureOptions,
  WebDefaultTrackingFlags,
} from './webviewAutocaptureOwnership';
export {
  applyNativeWebViewAutocaptureGuards,
  WEB_LEGACY_NATIVE_HOSTED_DEFAULT_TRACKING,
} from './webviewAutocaptureOwnership';

export const buildStandaloneWebAutocapture = (): WebAutocaptureOptions => ({
  pageViews: true,
  elementInteractions: true,
  sessions: true,
  formInteractions: false,
  fileDownloads: false,
  attribution: false,
});

/** Block dashboard remote autocapture overrides when local init must stay authoritative. */
const REMOTE_AUTOCAPTURE_CONFIG_OFF = {
  fetchRemoteConfig: false,
  remoteConfig: { fetchRemoteConfig: false },
} as const;

export interface ResolveWebBrowserAmplitudeInitInput {
  amplitudeAutocaptureEnabled: boolean;
  isNativeWebView: boolean;
  nativeIds?: { deviceId: string; sessionId: number };
}

export interface ResolvedWebBrowserAmplitudeInit {
  deviceId?: string;
  sessionId?: number;
  defaultTracking?: WebDefaultTrackingFlags | boolean;
  autocapture?: WebAutocaptureOptions | boolean;
  fetchRemoteConfig?: boolean;
  remoteConfig?: { fetchRemoteConfig?: boolean };
}

/**
 * Pre-migration standalone / ID-less WebView: legacy `defaultTracking` only.
 * Do not pass `autocapture: false` — in Browser SDK 2.10+ that overrides and disables default tracking.
 */
const legacyBrowserTrackingInit = (): ResolvedWebBrowserAmplitudeInit => ({
  defaultTracking: true,
  ...REMOTE_AUTOCAPTURE_CONFIG_OFF,
});

/**
 * Browser SDK init overrides for Pathway web (standalone + native-hosted WebView).
 */
export const resolveWebBrowserAmplitudeInit = ({
  amplitudeAutocaptureEnabled,
  isNativeWebView,
  nativeIds,
}: ResolveWebBrowserAmplitudeInitInput): ResolvedWebBrowserAmplitudeInit => {
  if (!amplitudeAutocaptureEnabled) {
    if (isNativeWebView && nativeIds) {
      return {
        ...nativeIds,
        defaultTracking: WEB_LEGACY_NATIVE_HOSTED_DEFAULT_TRACKING,
        ...REMOTE_AUTOCAPTURE_CONFIG_OFF,
      };
    }
    return legacyBrowserTrackingInit();
  }

  const autocapture = applyNativeWebViewAutocaptureGuards(
    buildStandaloneWebAutocapture(),
    isNativeWebView,
    true,
  );

  if (isNativeWebView) {
    return {
      ...(nativeIds ?? {}),
      autocapture,
      defaultTracking: WEB_NATIVE_HOSTED_DEFAULT_TRACKING,
      ...REMOTE_AUTOCAPTURE_CONFIG_OFF,
    };
  }

  return {
    autocapture,
    defaultTracking: false,
  };
};
